import { LoadingButton } from "@mui/lab";
import { Tooltip } from "@mui/material";
import { GridApiPremium } from "@mui/x-data-grid-premium/models/gridApiPremium";
import { AnimatePresence, motion } from "motion/react";
import { RefObject } from "react";
import { useTranslation } from "react-i18next";
import { TbCancel, TbInfoCircle, TbProgressCheck, TbTableRow } from "react-icons/tb";

import { useClinicArrivalTasksPickUpSaveAction } from "@/hooks/useClinicArrivalTasksPickUpSaveAction";
import {
    useClinicArrivalTasksGridActions,
    useClinicArrivalTasksGridChanges,
} from "@/stores/use-clinic-arrival-tasks-grid";

import ClinicArrivalTasksUpdateDialog from "./ClinicArrivalTasksUpdateDialog";

export type ClinicArrivalTasksChangesButtonProps = {
    apiRef: RefObject<GridApiPremium | null>;
};

function ClinicArrivalTasksChangesButton({ apiRef }: ClinicArrivalTasksChangesButtonProps) {
    const { t } = useTranslation();
    const changes = useClinicArrivalTasksGridChanges();
    const { handleSaveChanges, isPending, saveDisabled, totals } = useClinicArrivalTasksPickUpSaveAction(apiRef);
    const { handleRowModesModelCancel } = useClinicArrivalTasksGridActions();

    const handleCancelChanges = () => {
        if (isPending) return;
        const grid = apiRef.current;
        if (!grid) return;
        for (const id of Object.keys(changes)) {
            void handleRowModesModelCancel(id, grid, true);
        }
    };

    return (
        <>
            <div className="flex items-end gap-1">
                <AnimatePresence>
                    {!!totals.row && (
                        <>
                            <Tooltip title={t("clinicArrival.saveShortcutHint")}>
                                <span>
                                    <LoadingButton
                                        className="max-w-fit grow-0 shrink-0"
                                        component={motion.div}
                                        initial={{
                                            y: -100,
                                            opacity: 0,
                                        }}
                                        animate={{
                                            opacity: 1,
                                            y: 0,
                                        }}
                                        exit={{ y: 20, opacity: 0 }}
                                        variant="contained"
                                        color="primary"
                                        disabled={saveDisabled}
                                        loading={isPending}
                                        fullWidth
                                        onClick={handleSaveChanges}
                                        startIcon={<TbProgressCheck />}
                                    >
                                        {t("buttons.save")}
                                    </LoadingButton>
                                </span>
                            </Tooltip>
                            <LoadingButton
                                className="max-w-fit grow-0 shrink-0"
                                component={motion.div}
                                initial={{
                                    x: 100,
                                    opacity: 0,
                                }}
                                animate={{
                                    opacity: 1,
                                    x: 0,
                                }}
                                exit={{ y: 20, opacity: 0 }}
                                variant="outlined"
                                color="secondary"
                                disabled={totals.row === 0 || isPending}
                                fullWidth
                                onClick={handleCancelChanges}
                                startIcon={<TbCancel />}
                            >
                                {t("buttons.cancel")}
                            </LoadingButton>
                        </>
                    )}
                </AnimatePresence>
                <div className="flex min-h-[37px] shrink-0 grow-0 items-center gap-1 rounded bg-gray-100 px-2 py-1">
                    <TbTableRow className="translate-y-[1px]" /> {totals.row}
                    <Tooltip
                        title={
                            <div className="flex flex-col gap-1">
                                <div>{t("changeRowCount") + " " + totals.row}</div>
                                <div>{t("changeCellCount") + " " + totals.cell}</div>
                            </div>
                        }
                    >
                        <span className="!self-start !translate-x-1">
                            <TbInfoCircle size={12} />
                        </span>
                    </Tooltip>
                </div>
            </div>

            <ClinicArrivalTasksUpdateDialog apiRef={apiRef} />
        </>
    );
}

export default ClinicArrivalTasksChangesButton;
